import {computed, inject, Injectable, InjectionToken, OnDestroy, signal} from '@angular/core';
import {FeedbackReason, GENERIC_ERROR, MAX_MESSAGE_LENGTH, STRINGS} from './assistant.constants';
import {ChatActionRequest, ChatError, ChatErrorCode, ChatSource, ChatStreamEvent, ChatStreamService} from './chat-stream.service';
import {ChatBubble, MarkdownService, takeBubbles, toPlainText} from './markdown.service';

export interface UserTurn {
  readonly id: string;
  readonly role: 'user';
  readonly text: string;
}

/** Reserved for tool-call confirmation cards (SSE-CONTRACT.md); nothing produces these yet. */
export interface ChatAction extends ChatActionRequest {
  state: 'pending' | 'confirming' | 'done' | 'cancelled' | 'expired' | 'failed';
  resultMessage?: string;
}

export interface AssistantTurn {
  readonly id: string;
  readonly role: 'assistant';
  /** pending: waiting for the first token; streaming: tokens arriving; stopped: user pressed Stop. */
  state: 'pending' | 'streaming' | 'complete' | 'error' | 'stopped';
  /** Status line shown before the first token ("Searching assessor.lacounty.gov…"). */
  status: string | null;
  /** Raw streamed text (Markdown or HTML). */
  text: string;
  /** Sanitized HTML: a per-frame preview while streaming, then the server's final HTML. */
  html: string;
  sources: ChatSource[];
  /** The answer's "Next steps" list, lifted out of the final HTML (takeBubbles). */
  bubbles: ChatBubble[];
  /** From done; identifies the answer for feedback. */
  messageId: string | null;
  error: {code: ChatErrorCode; message: string; retryable: boolean} | null;
  feedback: 'up' | 'down' | null;
  reason: FeedbackReason | null;
  actions: ChatAction[];
  /** The user message that produced this answer, and the conversation token sent with it (regenerate). */
  question: string;
  sentConversation: string | null;
}

export type ChatTurn = UserTurn | AssistantTurn;

/** Schedules a render at most once per frame. Injectable so tests can run it synchronously. */
export const FRAME_SCHEDULER = new InjectionToken<(callback: () => void) => void>('FRAME_SCHEDULER', {
  providedIn: 'root',
  factory: () => (callback: () => void) => requestAnimationFrame(() => callback()),
});

const RETRYABLE: ReadonlySet<ChatErrorCode> = new Set(['rate_limited', 'busy', 'timeout', 'upstream_error', 'network']);

let nextId = 0;
const newId = (prefix: string) => `${prefix}-${++nextId}`;

/**
 * State of one chat widget. Provided by the panel component, so it lives as long as the page
 * (closing the panel keeps the conversation; New chat clears it).
 */
@Injectable()
export class ChatStore implements OnDestroy {
  private readonly api = inject(ChatStreamService);
  private readonly markdown = inject(MarkdownService);
  private readonly scheduleFrame = inject(FRAME_SCHEDULER);

  private readonly turnsState = signal<ChatTurn[]>([]);
  private readonly conversationState = signal<string | null>(null);
  private abort: AbortController | null = null;
  private renderQueued = new Set<string>();

  readonly turns = this.turnsState.asReadonly();
  readonly conversation = this.conversationState.asReadonly();
  readonly isEmpty = computed(() => this.turnsState().length === 0);
  readonly busy = computed(() => this.turnsState().some(t => t.role === 'assistant' && (t.state === 'pending' || t.state === 'streaming')));
  readonly latestAssistantId = computed(() => [...this.turnsState()].reverse().find(t => t.role === 'assistant')?.id ?? null);

  /** Completed answers and errors, for the polite live region. */
  readonly announcement = signal('');
  /** Status changes (once per turn) and small confirmations, for the role="status" region. */
  readonly statusAnnouncement = signal('');

  ngOnDestroy(): void {
    this.stop();
  }

  async send(text: string): Promise<void> {
    const message = text.trim();
    if (!message || message.length > MAX_MESSAGE_LENGTH || this.busy()) {
      return;
    }

    this.cancelPendingActions();
    const assistant = this.newAssistantTurn(message);
    this.turnsState.update(turns => [...turns, {id: newId('user'), role: 'user', text: message}, assistant]);
    await this.run(assistant.id, message, this.conversationState(), true);
  }

  /** Re-asks the latest question from the conversation state before it, replacing the answer. */
  async regenerate(turnId: string): Promise<void> {
    const turn = this.assistant(turnId);
    if (!turn || this.busy() || turnId !== this.latestAssistantId()) {
      return;
    }

    this.reset(turnId);
    await this.run(turnId, turn.question, turn.sentConversation, true);
  }

  /** "Try again" on an error: same as regenerate. */
  retry(turnId: string): Promise<void> {
    return this.regenerate(turnId);
  }

  stop(): void {
    this.abort?.abort();
  }

  newChat(): void {
    this.stop();
    this.turnsState.set([]);
    this.conversationState.set(null);
    this.announcement.set('');
    this.statusAnnouncement.set('');
  }

  rate(turnId: string, rating: 'up' | 'down'): void {
    const turn = this.assistant(turnId);
    if (!turn?.messageId) {
      return;
    }

    if (turn.feedback === rating) {
      this.patch(turnId, {feedback: null, reason: null});
      return;
    }

    this.patch(turnId, {feedback: rating, reason: null});
    void this.api.feedback(turn.messageId, rating);
    if (rating === 'up') {
      this.status(STRINGS.thanks);
    }
  }

  /** Optional one-tap reason after a thumbs-down. */
  giveReason(turnId: string, reason: FeedbackReason): void {
    const turn = this.assistant(turnId);
    if (!turn?.messageId || turn.feedback !== 'down' || turn.reason) {
      return;
    }

    this.patch(turnId, {reason});
    void this.api.feedback(turn.messageId, 'down', reason);
    this.status(STRINGS.thanks);
  }

  /**
   * Confirm or cancel a pending action (reserved; see SSE-CONTRACT.md). The server executes it
   * after checking the single-use token against the session, then streams the follow-up.
   */
  async decideAction(turnId: string, actionId: string, decision: 'confirm' | 'cancel'): Promise<void> {
    const action = this.assistant(turnId)?.actions.find(a => a.actionId === actionId);
    if (!action || action.state !== 'pending' || this.busy()) {
      return;
    }

    this.patchAction(turnId, actionId, {state: 'confirming'});
    const followUp = this.newAssistantTurn('');
    this.turnsState.update(turns => [...turns, followUp]);
    await this.consume(
      followUp.id,
      this.api.stream('/actions/decide', {actionId, decision, conversation: this.conversationState()}, this.begin().signal),
      false,
      turnId);
  }

  private async run(turnId: string, message: string, conversation: string | null, retryIfExpired: boolean): Promise<void> {
    this.patch(turnId, {sentConversation: conversation});
    const controller = this.begin();
    const retried = await this.consume(turnId, this.api.stream('/stream', {message, conversation}, controller.signal), retryIfExpired);
    if (retried) {
      this.reset(turnId);
      await this.run(turnId, message, null, false);
    }
  }

  private begin(): AbortController {
    this.abort?.abort();
    const controller = new AbortController();
    this.abort = controller;
    return controller;
  }

  /** Applies a stream to a turn. Returns true when the caller should retry with a new conversation. */
  private async consume(turnId: string, events: AsyncIterable<ChatStreamEvent>, retryIfExpired: boolean, actionTurnId?: string): Promise<boolean> {
    const controller = this.abort;
    let announcedStatus = false;
    try {
      for await (const event of events) {
        switch (event.type) {
          case 'status':
            this.patch(turnId, {status: event.text});
            if (!announcedStatus) {
              announcedStatus = true;
              this.status(event.text);
            }
            break;
          case 'delta':
            this.patch(turnId, {state: 'streaming', text: (this.assistant(turnId)?.text ?? '') + event.text});
            this.queueRender(turnId);
            break;
          case 'sources':
            this.patch(turnId, {sources: this.markdown.allowedSources(event.sources)});
            break;
          case 'done':
            this.complete(turnId, event);
            break;
          case 'error':
            throw new ChatError(event.code, event.message, event.correlationId);
          case 'action_request':
            this.patch(turnId, {actions: [...(this.assistant(turnId)?.actions ?? []), {...event.action, state: 'pending'}]});
            break;
          case 'action_result':
            if (actionTurnId) {
              this.patchAction(actionTurnId, event.actionId, {state: event.status, resultMessage: event.message});
            }
            break;
          default:
            // Unknown events are ignored so the server can add new ones.
            break;
        }
      }
      return false;
    } catch (error) {
      if (controller?.signal.aborted) {
        const turn = this.assistant(turnId);
        if (turn && (turn.state === 'pending' || turn.state === 'streaming')) {
          this.patch(turnId, {state: 'stopped', status: null, html: this.markdown.render(turn.text)});
        }
        return false;
      }

      const failure = error instanceof ChatError ? error : new ChatError('upstream_error', GENERIC_ERROR);
      if (failure.code === 'conversation_expired' || failure.code === 'conversation_limit') {
        this.conversationState.set(null);
        if (failure.code === 'conversation_expired' && retryIfExpired) {
          return true;
        }
      }

      this.patch(turnId, {
        state: 'error',
        status: null,
        error: {code: failure.code, message: failure.message || GENERIC_ERROR, retryable: RETRYABLE.has(failure.code)},
      });
      if (actionTurnId) {
        this.turnsState.update(turns => turns.map(t => t.id === actionTurnId && t.role === 'assistant'
          ? {...t, actions: t.actions.map(a => a.state === 'confirming' ? {...a, state: 'failed' as const} : a)}
          : t));
      }
      this.announce(failure.message || GENERIC_ERROR);
      return false;
    } finally {
      if (this.abort === controller) {
        this.abort = null;
      }
    }
  }

  private complete(turnId: string, event: Extract<ChatStreamEvent, {type: 'done'}>): void {
    this.renderQueued.delete(turnId);
    const {html, bubbles} = takeBubbles(this.markdown.sanitize(event.html));
    this.conversationState.set(event.conversation || null);
    this.patch(turnId, {state: 'complete', status: null, html, bubbles, messageId: event.messageId || null});
    this.announce(`${STRINGS.saidPrefix} ${toPlainText(html)}`);
  }

  private queueRender(turnId: string): void {
    if (this.renderQueued.has(turnId)) {
      return;
    }
    this.renderQueued.add(turnId);
    this.scheduleFrame(() => {
      if (!this.renderQueued.delete(turnId)) {
        return;
      }
      const turn = this.assistant(turnId);
      if (turn?.state === 'streaming') {
        this.patch(turnId, {html: this.markdown.render(turn.text)});
      }
    });
  }

  private cancelPendingActions(): void {
    this.turnsState.update(turns => turns.map(t => t.role === 'assistant' && t.actions.some(a => a.state === 'pending')
      ? {...t, actions: t.actions.map(a => a.state === 'pending' ? {...a, state: 'cancelled' as const} : a)}
      : t));
  }

  private newAssistantTurn(question: string): AssistantTurn {
    return {
      id: newId('assistant'), role: 'assistant', state: 'pending', status: null, text: '', html: '', sources: [], bubbles: [],
      messageId: null, error: null, feedback: null, reason: null, actions: [], question, sentConversation: null,
    };
  }

  private reset(turnId: string): void {
    this.renderQueued.delete(turnId);
    this.patch(turnId, {state: 'pending', status: null, text: '', html: '', sources: [], bubbles: [], messageId: null, error: null, feedback: null, reason: null, actions: []});
  }

  private assistant(turnId: string): AssistantTurn | undefined {
    const turn = this.turnsState().find(t => t.id === turnId);
    return turn?.role === 'assistant' ? turn : undefined;
  }

  private patch(turnId: string, changes: Partial<AssistantTurn>): void {
    this.turnsState.update(turns => turns.map(t => t.id === turnId && t.role === 'assistant' ? {...t, ...changes} : t));
  }

  private patchAction(turnId: string, actionId: string, changes: Partial<ChatAction>): void {
    const turn = this.assistant(turnId);
    if (turn) {
      this.patch(turnId, {actions: turn.actions.map(a => a.actionId === actionId ? {...a, ...changes} : a)});
    }
  }

  private announce(text: string): void {
    // Clear first so the same text is announced again if repeated.
    this.announcement.set('');
    setTimeout(() => this.announcement.set(text), 100);
  }

  /** Polite status announcement (e.g. "Copied to clipboard"). */
  notify(text: string): void {
    this.status(text);
  }

  private status(text: string): void {
    this.statusAnnouncement.set('');
    setTimeout(() => this.statusAnnouncement.set(text), 50);
  }
}
